import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MenuCloneModal from '../../../MenuCloneModal';
import { store } from '@app/state/store';
import {
  CONFIRMATION_MODAL_INITIAL_STATE,
  FormMode,
  IMenuStore,
  IMenuType,
  ISubcategory,
  POST_REQUEST_MENU,
  SectionId,
} from '@westondev/tableturn-core';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { actionCreatorsApp, actionCreatorsMenuWeb } from '@app/state';
import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import useRefreshMenuWeb from '@app/hooks/useRefreshMenuWeb';
import {
  makeSelectItemData,
  selectIsMenuCreationMode,
  selectItems,
  selectMenuMode,
} from '@app/state/menu/menuSelectors';

interface SectionOptionsProps {
  t: (key: string, options?: any) => string;
  bucket: keyof Omit<IMenuStore, 'hotButtons'>;
  isMasterMode: boolean;
  itemName?: string;
  itemId?: number | string;
}

const SECTION_ID = SectionId.BASIC;

const SectionOptions = ({
  t,
  bucket,
  isMasterMode,
  itemName,
}: SectionOptionsProps) => {
  const dispatch = useDispatch();
  const onRefresh = useRefreshMenuWeb();

  const getMemoizedItemData = makeSelectItemData(bucket, SECTION_ID);

  const [isCloneModalActive, setIsCloneModalActive] = useState(false);

  const { itemData } = useSelector(getMemoizedItemData);
  const isCreation = useSelector(selectIsMenuCreationMode);
  const changeDataMode = useSelector(selectMenuMode);
  const items = useSelector(selectItems);

  const { setShowConfirmationModal, showToast } = bindActionCreators(
    actionCreatorsApp,
    dispatch,
  );

  const { deleteBucketElementWeb, checkForChangesAndNavigateWeb } =
    bindActionCreators(actionCreatorsMenuWeb, dispatch);

  const isMassUpdate = useMemo(() => {
    return changeDataMode === FormMode.MASS_UPDATE;
  }, [changeDataMode]);

  const handleDelete = useCallback(() => {
    if (bucket === 'menuTypes') {
      const { itemIds: itemsInMenuTypeIds } = itemData as IMenuType;
      const itemsInMasterMenu = items;

      let canDeleteMenuType = true;
      let itemsListString = '';

      for (const itemId of itemsInMenuTypeIds) {
        const currentItem = itemsInMasterMenu[itemId];

        if (!currentItem) continue;

        const menuTypeVersions = Object.keys(
          currentItem?.menuTypeVersions,
        ).length;

        if (menuTypeVersions < 2) {
          canDeleteMenuType = false;
          itemsListString =
            itemsListString.length > 0
              ? itemsListString + `\n${currentItem.name}`
              : currentItem.name;
        }
      }

      if (!canDeleteMenuType) {
        setShowConfirmationModal({
          active: true,
          title: t('app.modals.cannotDeleteMenuTypeModal.title'),
          description: `${t(
            'app.modals.cannotDeleteMenuTypeModal.description',
          )}\n\n${itemsListString}`,
          type: 'warning',
          name: itemData?.name,
          onSuccess: () => null,
          hideCancel: true,
        });
        return;
      }
    } else if (bucket === 'subcategories') {
      const { itemIds: assignedItemIds, comboIds: assignedComboIds } =
        itemData as ISubcategory;

      if (assignedItemIds.length || assignedComboIds.length) {
        setShowConfirmationModal({
          active: true,
          title: t('menuScreen.subcategoryDetails.basic.deleteModal.title'),
          type: 'warning',
          description: t(
            'menuScreen.subcategoryDetails.basic.deleteModal.body',
          ),
          hideSuccess: true,
          onSuccess: () => null,
        });
      }
      return;
    }
    setShowConfirmationModal({
      active: true,
      title: t(`menuScreen.common.bucket.${bucket}.singular`),
      type:
        bucket === 'discounts'
          ? isMasterMode
            ? 'delete'
            : 'unassociate'
          : 'delete',
      name: itemName ?? itemData.name,
      onSuccess: () => {
        setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
        deleteBucketElementWeb();
      },
    });
  }, [
    bucket,
    itemData,
    items,
    setShowConfirmationModal,
    t,
    isMasterMode,
    itemName,
    deleteBucketElementWeb,
  ]);

  const handleCloneConfirm = (newId: number) => {
    const currentMenu = store.getState().menu.selectedMode.currentMenu;
    checkForChangesAndNavigateWeb(() => {
      const name =
        currentMenu?.[bucket]?.[newId]?.name ?? itemName ?? itemData.name ?? '';

      dispatch({
        type: POST_REQUEST_MENU,
        payload: {
          item: {
            text: name,
            id: newId,
          },
          bucketSelected: bucket,
          isDeleting: false,
        },
      });
    }, onRefresh);
  };

  const showDeleteDisabled = () => {
    showToast({
      type: 'info',
      title: t('menuScreen.subcategoryMain.disabledActionToast.title'),
      description: t('menuScreen.deleteToast', {
        bucket: t(`menuScreen.common.bucket.${bucket}.plural`),
      }),
    });
  };

  return (
    <>
      <MenuCloneModal
        active={isCloneModalActive}
        bucket={bucket}
        onCancel={() => setIsCloneModalActive(false)}
        onClose={() => setIsCloneModalActive(false)}
        onClone={handleCloneConfirm}
        name={itemName ?? itemData.name ?? ''}
      />
      <Box
        csx={{
          display: 'flex',
          gap: '10px',
        }}>
        <Button
          variant="primary"
          csx={{ minWidth: '100px' }}
          disabled={isMassUpdate || isCreation}
          onClick={() => setIsCloneModalActive(true)}>
          {t('commonButtons.clone')}
        </Button>
        <Button
          variant="danger"
          csx={{ minWidth: '100px' }}
          disabled={!isMasterMode || isMassUpdate || isCreation}
          onClickDisabled={showDeleteDisabled}
          onClick={handleDelete}>
          {t('commonButtons.delete')}
        </Button>
      </Box>
    </>
  );
};

export default SectionOptions;
